import * as React from 'react';
import moment from 'moment';
import styles from './navbar.module.scss';
import Logo from './logo/logo.component';
import CollapseHelpComponent from './collapse-help/collapse-help.component';
import CollapseUserComponent from './collapse-user/collapse-user.component';
import CollapseSubMenuComponent from './collapse-sub-menu/collapse-sub-menu.component';
import { MdMoreHoriz } from "react-icons/md";
import { ReactComponent as HomeIcon } from "@/assets/home-icon-v2.svg";
import { FaCheck, FaUserCircle } from 'react-icons/fa';
import {
    Row,
    Col,
    Navbar as ReactstrapNavbar,
    Nav,
    NavbarBrand,
    NavbarToggler,
    Collapse,
    NavItem,
    NavLink,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import { ESIcon, ENIcon, PTIcon } from './lang-icons';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import INavbarItem from './models/navbar.model';
import ILanguage from './models/language.model';
import IUser from './models/user.model';
import { ISubMenuItem } from '@models/*';

const HOME_URL = 'https://ayudaenaccion.sharepoint.com/sites/Portal_Pre#/home';

interface INavbar {
    navbarItems: INavbarItem[];
    currentSiteUrl: string;
    urlLogo: string;
    applicationName: string;
    langs: ILanguage[];
    currentLang: ILanguage;
    onLanguageChange: (lang: ILanguage) => void;
    user: IUser;
    userImage?: string;
    filterGroups: (item: any) => boolean;
    proyectaFilter: (x: any) => boolean;
    getHelpFiles: (application: string) => any;
    routeHelpVisor: string;
    fetchSubMenuItems: (subMenuList: string) => Promise<ISubMenuItem[]>;
}

type NavbarProps = INavbar & RouteComponentProps;

const NavbarComponent: React.FC<NavbarProps> = ({
    navbarItems = [],
    currentSiteUrl,
    urlLogo,
    applicationName,
    langs,
    currentLang,
    onLanguageChange,
    user,
    userImage,
    filterGroups,
    proyectaFilter,
    getHelpFiles,
    routeHelpVisor,
    fetchSubMenuItems,
    history
}) => {
    const [menuIsOpen, setMenuIsOpen] = React.useState(true);
    const [helpFiles, setHelpFiles] = React.useState<any[]>([]);
    const [subMenuList, setSubMenuList] = React.useState<string | null>(null);
    const [subMenuTitle, setSubMenuTitle] = React.useState<string | null>(null);

    const groupedItems = React.useMemo(() => {
        return navbarItems.reduce<Record<string, INavbarItem[]>>((acc, item) => {
            const { Application } = item;
            if (!acc[Application]) {
                acc[Application] = [];
            }
            acc[Application].push(item);
            return acc;
        }, {});
    }, [navbarItems]);

    const redirectTo = React.useCallback((url: string) => {
        if (url.startsWith('http://') || url.startsWith('https://')) {
            window.location.href = url;
        } else {
            history.push(url);
        }
    }, [history]);

    const getIconForLang = (label: string) => {
        switch (label) {
            case 'ES': return <ESIcon />;
            case 'EN': return <ENIcon />;
            case 'PT': return <PTIcon />;
            default: return null;
        }
    };

    const handleHelpClick = async (application: string) => {
        try {
            const response = await getHelpFiles(application);
            if (response.OperationSuccess) {
                setHelpFiles(response.Data);
            } else {
                setHelpFiles([]);
            }
        } catch (error) {
            console.error("Error fetching help files:", error);
            setHelpFiles([]);
        }
    };

    const handleSubMenuClick = (subMenu: string | null, title: string) => {
        setSubMenuList(subMenu);
        setSubMenuTitle(title);
        document.getElementById('toggler-sub-menu-hidden')?.click();
    };

    const renderDropdowns = () => {
        const applications = Object.keys(groupedItems).filter((app) => {
            if (applicationName === 'PROYECTA' || applicationName === 'ADMINISTRACIÓN GLOBAL') {
                return app === applicationName;
            }
            return true;
        });

        return applications.map((application) => {
            const items = groupedItems[application].sort((a, b) => a.OCTAVIAOrder - b.OCTAVIAOrder);
            if (items.length === 1) {
                return (
                    <NavItem key={application}>
                        {application === 'ADMINISTRACIÓN GLOBAL' && applicationName === 'ADMINISTRACIÓN GLOBAL' ? (
                            <span className={`nav-link ${styles.NavText}`}>{items[0].OCTAVIAValorTraduccion}</span>
                                ) : (
                            <NavLink
                                className={`${styles.NavLink} ${application === applicationName ? styles.NavLinkActive : ''}`}
                                onClick={() => {
                                    const item = items[0];
                                    if (item.SubMenu) {
                                        handleSubMenuClick(item.SubMenu, item.OCTAVIAValorTraduccion);
                                    } else {
                                        redirectTo(
                                            item.Application === applicationName ? item.Url : item.BaseUrl + item.Url
                                        );
                                    }
                                }}
                            >
                                {items[0].OCTAVIAValorTraduccion}
                            </NavLink>
                        )}
                    </NavItem>
                );
            }

            return (
                <UncontrolledDropdown nav inNavbar key={application}>
                    <DropdownToggle nav className={`${styles.NavLink} ${application === applicationName ? styles.NavLinkActive : ''}`}>
                        {application}
                    </DropdownToggle>
                    <DropdownMenu>
                        {items.map((item) => {
                            if (application !== 'PROYECTA' || (application === 'PROYECTA' && proyectaFilter(item))) {
                                return (
                                    <DropdownItem
                                        className={styles.DropdownItem}
                                        key={item.ID}
                                        onClick={() => {
                                            if (item.SubMenu) {
                                                handleSubMenuClick(item.SubMenu, item.OCTAVIAValorTraduccion);
                                            } else if (item.MostrarAyuda) {
                                                handleHelpClick(application);
                                                document.getElementById('toggler-help-hidden')?.click();
                                            } else {
                                                redirectTo(item.Application === applicationName ? item.Url : item.BaseUrl + item.Url);
                                            }
                                        }}>
                                        {item.OCTAVIAValorTraduccion}
                                    </DropdownItem>
                                );
                            }
                            return null;
                        })}
                    </DropdownMenu>
                </UncontrolledDropdown>
            );
        });
    };

    const currentDate = moment(new Date()).format("DD MMMM YYYY");

    const handleHomeClick = React.useCallback(() => {
        window.location.href = HOME_URL;
    }, []);

    const getLangDropdownLabel = (label: string) => {
        switch (label) {
            case 'ES': return 'IDIOMA';
            case 'EN': return 'LANGUAGE';
            case 'PT': return 'IDIOMA';
            default: return 'IDIOMA';
        }
    };

    return (
        <div className={styles.Navbar}>
            <Row className={`bg-primary`} style={{ padding: '5px 20px' }}>
                <Col xs={12} lg={10} className="pr-lg-0">
                    <div className={styles.Menu}>
                        <ReactstrapNavbar dark expand="lg" className="m-0 p-0 bg-primary">
                            <Col xs={10} lg={2} className="pl-0 pr-0 pt-2 pb-2 d-inline-grid">
                                <NavbarBrand href={currentSiteUrl} className="p-0 m-0 align-top w-100">
                                    <Logo logoUrl={urlLogo} />
                                </NavbarBrand>
                            </Col>
                            <NavbarToggler className="text-white mr-7" onClick={() => setMenuIsOpen(!menuIsOpen)}>
                                <MdMoreHoriz />
                            </NavbarToggler>
                            <Collapse isOpen={!menuIsOpen} navbar>
                                <Nav navbar>
                                    {renderDropdowns()}
                                    <NavItem style={{ display: 'none' }}>
                                        <NavLink id="toggler-help-hidden">Hidden Help Toggle</NavLink>
                                    </NavItem>
                                    <NavItem style={{ display: 'none' }}>
                                        <NavLink id="toggler-sub-menu-hidden">Hidden Sub Menu Toggle</NavLink>
                                    </NavItem>
                                </Nav>
                            </Collapse>
                        </ReactstrapNavbar>
                    </div>
                </Col>
                <Col className="d-none d-lg-inline-block text-right pl-lg-0" xs={12} lg={2}>
                    <div className={styles.UserInfo}>
                        <div className={styles.TopLevel}>
                        <UncontrolledDropdown nav inNavbar className={styles.LangDropdown}>
                                <DropdownToggle nav caret className={`${styles.NavLink}`}>
                                    {getLangDropdownLabel(currentLang.label)}
                                </DropdownToggle>
                                <DropdownMenu>
                                    {langs.map((lang) => (
                                        <DropdownItem
                                            key={lang.id}
                                            className={styles.DropdownItem}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onLanguageChange(lang);
                                            }}
                                        >
                                            {getIconForLang(lang.label)}{" "}
                                            {lang.label}
                                            {lang.label === currentLang.label && (
                                                <span className="float-right">
                                                    <FaCheck />
                                                </span>
                                            )}
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </UncontrolledDropdown>
                            <div className={styles.HomeIcon} onClick={() => window.location.href = 'https://ayudaenaccion.sharepoint.com/sites/Portal_pre'}>
                                <HomeIcon width={24} height={24} />
                            </div>
                            {user?.displayName && (
                                <div className={styles.UserName} id="toggler-user">
                                    {user.displayName} {(!!userImage) ? (
                                    <img src={userImage} style={{
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        marginLeft: '10px'
                                    }} alt="profile image" />
                                ) : <FaUserCircle size={24} />}
                                </div>
                            )}
                        </div>
                        <div className={styles.BottomLevel}>
                            <div className={styles.CurrentDate}>
                                {currentDate.toUpperCase()}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            <CollapseHelpComponent routeHelpVisor={routeHelpVisor} docs={helpFiles} currentLang={currentLang}/>
            {!!user && <CollapseUserComponent filterGroups={filterGroups} currentLang={currentLang} user={user} userImage={userImage} />}
            <CollapseSubMenuComponent subMenuList={subMenuList || ''} Title={subMenuTitle || ''} fetchSubMenuItems={fetchSubMenuItems}/>
        </div>
    );
};

export default withRouter(NavbarComponent);
